/**
 * Wizard de primera configuración (Alta.dc.html, pantallas 6 y 6b).
 *
 * El admin recién verificado no aterriza en un back-office vacío: 4 pasos
 * — empresa y horario → primer centro → invitar equipo → QR — y su gente ya
 * puede fichar. Todo es saltable y todo se puede cambiar después.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AdminUserCreate,
  apiClient,
  apiMessage,
  createUser,
  createWorksite,
  fetchPrintableQr,
  getTenantStatus,
  getWorkPolicies,
  toWorksiteCreate,
  updateTenant,
  updateWorksite,
  updateWorkPolicy,
  Worksite,
  WorksiteFormValue,
} from '../../services/apiClient';
import { WorkerFormFields } from '../admin/WorkerFormFields';
import { WorksiteFormFields } from '../worksites/WorksiteFormFields';
import { Icon } from '../ui/Icon';
import { AuthLayout, CARD_SHADOW } from '../auth/AuthLayout';

export const WIZARD_DONE_KEY = 'kakuat-wizard-done';

const STEP_NAMES = ['', 'Empresa y horario', 'Primer centro', 'Invitar equipo', 'QR y kiosco'];
const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

const EMPTY_INVITE: AdminUserCreate = {
  email: '',
  first_name: '',
  last_name: '',
  role: 'EMPLOYEE',
  weekly_hours: 40,
  is_part_time: false,
};

type InvitedRow = {
  name: string;
  email: string;
  status: 'enviada' | 'no enviada' | 'QR pendiente' | 'ya existe' | 'error';
  message?: string;
};

export function WelcomeWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // paso 1
  const [company, setCompany] = useState('');
  const [cif, setCif] = useState('');
  const [weeklyHours, setWeeklyHours] = useState('40');
  const [days, setDays] = useState([true, true, true, true, true, false, false]);
  const policyId = useRef<number | null>(null);

  // paso 2 — mismo formulario que el panel (WorksiteFormFields), variante wizard.
  const [siteDraft, setSiteDraft] = useState<WorksiteFormValue>({ name: '', code: '', geo_radius_meters: 150 });
  const [worksite, setWorksite] = useState<Worksite | null>(null);

  // paso 3
  const [draft, setDraft] = useState<AdminUserCreate>(EMPTY_INVITE);
  const [invited, setInvited] = useState<InvitedRow[]>([]);
  const invitedCount = useRef(0);
  const set = <K extends keyof AdminUserCreate>(k: K, v: AdminUserCreate[K]) => setDraft((d) => ({ ...d, [k]: v }));

  // paso 4
  const [qrUrl, setQrUrl] = useState('');
  const [qrError, setQrError] = useState(false);

  useEffect(() => {
    getTenantStatus()
      .then((t) => {
        setCompany((c) => c || t.name);
        setCif((c) => c || t.cif || '');
      })
      .catch(() => undefined);
    getWorkPolicies()
      .then((ps) => { policyId.current = ps[0]?.id ?? null; })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    // El QR del centro, con auth (un <img src> no puede llevar el Bearer).
    // Si falla, se ve el error con su reintentar: el `catch(() => undefined)`
    // dejaba el paso 4 girando para siempre y el botón de imprimir muerto,
    // sin error — con la configuración ya guardada, la primera impresión del
    // producto era una pantalla colgada (ronda 3, r2b19[6]). El reintento
    // vacía qrUrl y este efecto vuelve a dispararse.
    if (step === 4 && worksite && !qrUrl) {
      setQrError(false);
      apiClient
        .get(`/worksites/${worksite.id}/qr/dynamic`, { responseType: 'blob' })
        .then((res) => setQrUrl(URL.createObjectURL(res.data as Blob)))
        .catch(() => setQrError(true));
    }
  }, [step, worksite, qrUrl]);

  const retryQr = () => {
    setQrUrl('');
    setQrError(false);
  };

  /**
   * Imprimir el QR de PAPEL con el blob autenticado (fetchPrintableQr +
   * objectURL), el mismo patrón que Centros: el dinámico caduca a los 30 s y
   * lo impreso no serviría para fichar ni una vez (ronda 3, r2b21[4] lo
   * arregló en el panel; el wizard imprimía el blob del dinámico). Pide su
   * propio blob con Bearer, así que funciona aunque el QR de pantalla no
   * haya cargado — y si el centro exige QR de pantalla, no hay versión de
   * papel y se dice.
   */
  const printQr = async () => {
    if (!worksite) return;
    if (worksite.require_dynamic_qr) {
      setError(
        'Este centro exige QR de pantalla (rota cada 30 s): no hay versión para imprimir. ' +
          'Pon una tablet mostrando el código.',
      );
      return;
    }
    setError('');
    let blob: Blob;
    try {
      blob = await fetchPrintableQr(worksite.id);
    } catch (err: unknown) {
      setError(apiMessage(err, 'No se pudo obtener el QR imprimible'));
      return;
    }
    const url = URL.createObjectURL(blob);
    const w = window.open('', '_blank');
    if (!w) {
      URL.revokeObjectURL(url);
      return;
    }
    w.document.write(`
      <title>QR · ${worksite.name}</title>
      <div style="font-family:system-ui;text-align:center;padding:40px">
        <h1 style="margin:0 0 4px">${worksite.name}</h1>
        <p style="margin:0 0 24px;color:#666">Escanea para fichar · Kakuat</p>
        <img src="${url}" width="360" height="360"
             onload="setTimeout(()=>print(),300)">
        <p style="color:#999;font-size:12px">Código: ${worksite.code}</p>
        <p style="color:#999;font-size:12px">Este código exige ubicación: hay que estar en el centro.</p>
      </div>`);
    w.document.close();
  };

  const finish = () => {
    localStorage.setItem(WIZARD_DONE_KEY, '1');
    setDone(true);
  };

  const skip = () => (step >= 4 ? finish() : setStep(step + 1));

  const next = async () => {
    setError('');
    setBusy(true);
    try {
      if (step === 1) {
        const workdayCount = days.filter(Boolean).length || 5;
        const weekly = parseFloat(weeklyHours.replace(',', '.')) || 40;
        // ⚠️ Un NIF de menos de 6 caracteres se DESCARTABA en silencio y el
        // wizard avanzaba con el botón en verde: el admin creía que su empresa
        // estaba dada de alta con su NIF, y el informe para Inspección daba 409
        // durante meses sin más aviso que un banner de una pantalla a la que
        // no vuelve nadie después del alta. Ahora se dice y no se pasa.
        if (cif.trim().length < 6) {
          setError('Falta el NIF de la empresa: sin él no se puede emitir el informe para Inspección.');
          setBusy(false);
          return;
        }
        const patch: { name?: string; cif?: string } = { cif: cif.trim() };
        if (company.trim().length >= 2) patch.name = company.trim();
        await updateTenant(patch);
        if (policyId.current != null) {
          await updateWorkPolicy(policyId.current, {
            // Tope legal del art. 34.3 ET: 9 h ordinarias/día. El clamp del
            // backend (`LEGAL_MAX_DAILY_ORDINARY_HOURS`) recorta a 9 en
            // silencio y el wizard mandaba 12: con 60 h/5 días el admin creía
            // haber configurado 12 h diarias y lo grabado era 9 sin aviso
            // (ronda 4, r2b14[7]). Aquí se acota ANTES de enviar, con el
            // mismo número que la ley y el backend.
            standard_daily_hours: Math.min(9, +(weekly / workdayCount).toFixed(2)),
          });
        }
        setStep(2);
      } else if (step === 2) {
        if (!siteDraft.name.trim()) { setError('Ponle nombre al centro.'); return; }
        // Editar el centro tras «Atrás» se descartaba en silencio: el
        // `if (!worksite)` saltaba crear Y actualizar, así que el nombre
        // corregido no se guardaba y el paso 4 (y su QR) seguía mostrando el
        // viejo — había que arreglarlo en Centros sin que nada lo avisara
        // (ronda 3, r2b14[8]). El centro que ya existe se actualiza con el
        // mismo PUT que usa el panel.
        if (!worksite) {
          // El wizard ahora captura ubicación real (ciudad / pin / lat-lon),
          // así que la geovalla se exige de verdad — pero solo si hay
          // coordenadas (mismo patrón que el panel: una geovalla sin ubicar
          // no bloquea a nadie). Al terminar, el centro ya ficha sin volver
          // a Centros.
          const created = await createWorksite(toWorksiteCreate(siteDraft));
          setWorksite(created);
        } else {
          const updated = await updateWorksite(worksite.id, toWorksiteCreate(siteDraft));
          setWorksite(updated);
        }
        setStep(3);
      } else if (step === 3) {
        setStep(4);
      } else {
        finish();
      }
    } catch (err: unknown) {
      const apiError = (err as { apiError?: { message: string } }).apiError;
      setError(apiError?.message ?? 'Algo ha fallado. Inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  const addInvite = async () => {
    setError('');
    // El email NO es obligatorio: el backend trata el alta sin correo como
    // flujo de primera clase (token de activación + QR + PIN, feature del
    // 06/09). Exigirlo aquí dejaba al peón sin buzón fuera del wizard — la
    // mitad del mercado al que apunta el producto, invitada sólo desde la
    // ficha de Trabajadores (ronda 4, r2b19[5]).
    if (!draft.first_name?.trim() || !draft.last_name?.trim()) {
      setError('Nombre y apellidos son obligatorios.');
      return;
    }
    // Sin correo sólo vale un trabajador: los responsables necesitan correo
    // para los avisos (el backend lo rechaza con 422; aquí se evita el viaje).
    if (!draft.email?.trim() && draft.role !== 'EMPLOYEE') {
      setError('Sin correo sólo se puede invitar a trabajadores: un responsable necesita correo para los avisos.');
      return;
    }
    // El `required` del input no dispara: el wizard no usa <form>, se invita
    // con botón onClick. Sin fecha de alta, el motor devenga el año completo
    // de vacaciones — la API también la exige, aquí se evita el viaje.
    if (!draft.hire_date) {
      setError('La fecha de alta es obligatoria: de ella depende el devengo de vacaciones.');
      return;
    }
    setBusy(true);
    const name = `${draft.first_name} ${draft.last_name}`.trim();
    const email = draft.email?.trim() ?? '';
    try {
      const creado = await createUser({ ...draft, email: email || undefined, password: undefined });
      invitedCount.current += 1;
      // El envío es best-effort: el backend dice en `email_sent` si el correo
      // salió de verdad. Pintar «enviada» cuando el SMTP falló dejaba el alta
      // hecha… y la invitación en ningún buzón, creyendo que estaba enviada.
      if (creado.email_sent === false) {
        setInvited((rows) => [
          ...rows,
          {
            name,
            email,
            status: 'no enviada',
            message:
              'No se pudo enviar el correo — reenvíalo desde su ficha en Trabajadores',
          },
        ]);
      } else if (!email) {
        // Sin correo no hay nada que "enviar": su credencial es el QR de
        // activación, que se muestra (o se reemite) desde la ficha en
        // Trabajadores. Decir "invitación enviada" sería inventar un correo.
        setInvited((rows) => [
          ...rows,
          {
            name,
            email,
            status: 'QR pendiente',
            message: 'Sin correo: activa su acceso con el QR de su ficha en Trabajadores',
          },
        ]);
      } else {
        setInvited((rows) => [...rows, { name, email, status: 'enviada' }]);
      }
      setDraft(EMPTY_INVITE);
    } catch (err: unknown) {
      const apiError = (err as { apiError?: { status: number; message: string } }).apiError;
      const status = apiError?.status === 409 ? 'ya existe' : 'error';
      setInvited((rows) => [...rows, { name, email, status, message: status === 'error' ? apiError?.message : undefined }]);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        <div
          className="w-full max-w-[520px] self-start mt-[2vh] bg-canvas border border-line-strong rounded-3xl px-7 py-10 sm:px-11 sm:py-12 text-center animate-rise"
          style={CARD_SHADOW}
        >
          <div className="w-[88px] h-[88px] rounded-3xl bg-brand-soft grid place-items-center mx-auto mb-6 text-brand">
            <Icon name="check-circle" size={44} strokeWidth={1.7} />
          </div>
          <h1 className="font-display font-extrabold text-[26px] sm:text-[32px] tracking-tight mb-3">
            Tu equipo ya puede fichar
          </h1>
          <p className="text-ink-2 text-[16px] leading-relaxed mx-auto mb-7 max-w-[38ch]">
            {worksite ? `Has creado el centro ${worksite.name}` : 'Configuración guardada'}
            {invitedCount.current > 0
              ? ` e invitado a ${invitedCount.current} persona${invitedCount.current === 1 ? '' : 's'}.`
              : '.'}{' '}
            {worksite ? 'El QR está listo para imprimir.' : ''}
          </p>
          <button onClick={() => navigate('/admin/workers')} className="btn-primary">
            Ir al panel
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div
        className="w-full max-w-[720px] self-start bg-canvas border border-line-strong rounded-3xl overflow-hidden animate-rise"
        style={CARD_SHADOW}
      >
        {/* cabecera + progreso */}
        <div className="pt-6 px-6 sm:px-11">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <span className="w-[11px] h-[11px] rounded-full bg-brand" />
              <span className="font-display font-black text-[19px] tracking-tight">kakuat</span>
            </div>
            {step < 4 && (
              <button onClick={skip} className="text-ink-3 text-[14px] font-semibold">
                Saltar por ahora
              </button>
            )}
          </div>
          <div className="flex gap-2 mb-2">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className={`flex-1 h-1.5 rounded-full transition-colors duration-300 ${n <= step ? 'bg-brand' : 'bg-surface-3'}`}
              />
            ))}
          </div>
          <div className="font-mono text-[12px] text-ink-3 uppercase tracking-wider">
            Paso {step} de 4 · {STEP_NAMES[step]}
          </div>
        </div>

        <div className="px-6 sm:px-11 py-6 sm:pb-10">
          {error && (
            <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px] mb-4">{error}</p>
          )}

          {step === 1 && (
            <>
              <h1 className="font-display font-extrabold text-[26px] tracking-tight mb-2">Tu empresa</h1>
              <p className="text-ink-2 text-[15px] mb-5">Lo básico para empezar. Podrás cambiarlo cuando quieras.</p>
              <div className="flex flex-col gap-4">
                <label className="flex flex-col gap-1.5">
                  <span className="label !mb-0">Nombre de la empresa</span>
                  <input className="field bg-surface" value={company} maxLength={120} onChange={(e) => setCompany(e.target.value)} />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="label !mb-0">NIF</span>
                  <input className="field bg-surface" placeholder="B12345678" value={cif} maxLength={20} onChange={(e) => setCif(e.target.value)} />
                  <span className="text-[13px] text-ink-soft">
                    Va en el informe para Inspección: identifica a la empresa que lleva el registro.
                  </span>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="label !mb-0">Jornada semanal (h)</span>
                  <input className="field bg-surface" inputMode="decimal" value={weeklyHours} onChange={(e) => setWeeklyHours(e.target.value)} />
                  <span className="text-[13px] text-ink-soft">
                    De aquí sale la jornada diaria, con el tope legal de 9 h/día (art. 34.3 ET).
                  </span>
                </label>
                <div>
                  <span className="label">Horario habitual</span>
                  <div className="flex gap-2.5 flex-wrap">
                    {WEEKDAYS.map((label, i) => {
                      const on = days[i];
                      return (
                        <button
                          key={label}
                          type="button"
                          onClick={() => setDays((d) => d.map((v, j) => (j === i ? !v : v)))}
                          className={`w-11 h-11 rounded-xl border-[1.5px] font-bold text-[14px] transition-colors ${
                            on ? 'bg-brand text-on-brand border-brand' : 'bg-surface text-ink-3 border-line-strong'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="font-display font-extrabold text-[26px] tracking-tight mb-2">Tu primer centro de trabajo</h1>
              <p className="text-ink-2 text-[15px] mb-5">Donde ficha tu equipo. Ubícalo ahora y ya podrá fichar. Puedes añadir más después.</p>
              <div className="card-surface p-5">
                <WorksiteFormFields value={siteDraft} onChange={(p) => setSiteDraft((d) => ({ ...d, ...p }))} variant="wizard" />
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="font-display font-extrabold text-[26px] tracking-tight mb-2">Invita a tu equipo</h1>
              <p className="text-ink-2 text-[15px] mb-5">
                Si tienen correo, les llega una invitación por email para crear su
                contraseña. Si no, nada de lo que esperar: se les da un QR de
                activación que escanean con el móvil y entran con su PIN.
              </p>
              <div className="card-surface p-5">
                <WorkerFormFields draft={draft} set={set} mode="create" hidePassword />
              </div>
              <button
                type="button"
                onClick={addInvite}
                disabled={busy}
                className="self-start flex items-center gap-2 border-[1.5px] border-dashed border-line-strong rounded-xl px-4 py-3 text-ink-2 font-semibold text-[14.5px] mt-3 disabled:opacity-40"
              >
                <Icon name="plus" size={18} strokeWidth={2} />
                Añadir e invitar
              </button>

              {invited.length > 0 && (
                <div className="flex flex-col gap-2 mt-5">
                  {invited.map((row, i) => (
                    <div key={i} className="flex gap-2.5 items-center justify-between text-[14px]">
                      <span className="text-ink-2 truncate">
                        {row.name} <span className="text-ink-3">· {row.email || 'sin correo'}</span>
                      </span>
                      <span
                        className={`font-mono text-[11px] whitespace-nowrap ${
                          row.status === 'enviada' ? 'text-brand' : 'text-signal'
                        }`}
                        title={row.message}
                      >
                        {row.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <h1 className="font-display font-extrabold text-[26px] tracking-tight mb-2">
                Casi listo: el QR del centro
              </h1>
              <p className="text-ink-2 text-[15px] mb-5">
                Imprímelo y pégalo en la entrada. Tu equipo lo escanea para fichar. Rota cada 30 s: es anti-foto.
              </p>
              {worksite ? (
                <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-center bg-surface border border-line rounded-2xl p-5">
                  <div className="w-[130px] h-[130px] rounded-xl bg-white p-3 mx-auto sm:mx-0">
                    {qrUrl ? (
                      <img src={qrUrl} alt={`QR de ${worksite.name}`} className="w-full h-full" />
                    ) : qrError ? (
                      /* Sin QR no hay spinner eterno: el fallo se dice y se
                         reintenta; el resto del paso (imprimir, terminar)
                         sigue usable. */
                      <div className="w-full h-full grid place-items-center text-center">
                        <div>
                          <p className="text-[11.5px] font-semibold text-danger mb-1.5">
                            El QR no cargó
                          </p>
                          <button
                            type="button"
                            onClick={retryQr}
                            className="text-[12px] font-semibold text-ink-2 underline"
                          >
                            Reintentar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full grid place-items-center">
                        <div className="w-6 h-6 rounded-full border-[3px] border-brand border-t-transparent animate-spin" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 text-center sm:text-left">
                    <div className="font-bold text-[16px] mb-1">{worksite.name}</div>
                    {siteDraft.address && <div className="font-mono text-[12px] text-ink-3 mb-4">{siteDraft.address}</div>}
                    <button
                      type="button"
                      onClick={() => printQr()}
                      className="inline-flex items-center gap-2 h-[46px] px-4.5 rounded-xl bg-brand text-on-brand font-bold text-[14.5px] mb-2.5"
                    >
                      <Icon name="printer" size={18} strokeWidth={1.8} />
                      Imprimir QR
                    </button>
                    <p className="text-[12.5px] text-ink-3">
                      El kiosco (tablet compartida) se activa desde Centros de trabajo.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-ink-2 text-[15px] bg-surface border border-line rounded-2xl p-5">
                  Saltaste la creación del centro. Podrás crearlo (y su QR) desde el panel, en Centros de trabajo.
                </p>
              )}
            </>
          )}

          {/* navegación */}
          <div className="flex gap-3 mt-7">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} disabled={busy} className="h-[54px] px-6 rounded-[13px] border-[1.5px] border-line-strong text-ink font-semibold text-[16px]">
                Atrás
              </button>
            )}
            <button onClick={next} disabled={busy} className="flex-1 h-[54px] rounded-[13px] bg-brand text-on-brand font-bold text-[16.5px] disabled:opacity-40">
              {busy ? 'Guardando…' : step >= 4 ? 'Terminar' : 'Continuar'}
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
